import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { kytService } from '../../services/kytService';

// Types
export interface KYTAnalysis {
  address: string;
  blockchain: string;
  addressType: string;
  riskScore: number;
  riskLevel: string;
  riskFactors: Record<string, any>;
  isSanctioned: boolean;
  isExchange: boolean;
  exchangeName?: string;
  totalReceived: number;
  totalSent: number;
  netBalance: number;
  transactionCount: number;
  firstSeen: string;
  lastUpdated: string;
  transactions: any[];
  analysisTimestamp: string;
}

export interface TransactionTrace {
  traceId: string;
  rootAddress: string;
  blockchain: string;
  maxSteps: number;
  actualSteps: number;
  totalAmount: number;
  riskScore: number;
  traceStatus: string;
  startTime: string;
  endTime: string;
  traceSteps: TraceStep[];
  highRiskAddresses: string[];
  exchangeAddresses: string[];
  sanctionsAddresses: string[];
  summary: any;
}

export interface TraceStep {
  step: number;
  address: string;
  fromAddress?: string;
  toAddress?: string;
  transactionHash: string;
  amount: number;
  timestamp: string;
  addressType: string;
  riskScore: number;
  isHighRisk: boolean;
  isExchange: boolean;
  isSanctioned: boolean;
  exchangeName?: string;
  direction?: string; // "in", "out", or "transfer" relative to root address
}

export interface AddressValidation {
  address: string;
  blockchain: string;
  isValid: boolean;
  detectedBlockchain: string | null;
}

export interface KYTProgress {
  step: string;
  progress: number;
  message: string;
  isComplete: boolean;
}

interface KYTState {
  // Current search state
  currentAddress: string;
  currentBlockchain: string;
  isValidating: boolean;
  isAnalyzing: boolean;
  isTracing: boolean;
  
  // Results
  validation: AddressValidation | null;
  analysis: KYTAnalysis | null;
  trace: TransactionTrace | null;
  
  // Progress tracking
  progress: KYTProgress | null;
  
  // Error handling
  error: string | null;
  
  // Cache for analyzed addresses
  analysisCache: Record<string, KYTAnalysis>;
  traceCache: Record<string, TransactionTrace>;
  validationCache: Record<string, AddressValidation>;
  
  // History
  searchHistory: Array<{
    address: string;
    blockchain: string;
    timestamp: string;
    type: 'validation' | 'analysis' | 'trace';
  }>;
}

const initialState: KYTState = {
  currentAddress: '',
  currentBlockchain: 'ETHEREUM',
  isValidating: false,
  isAnalyzing: false,
  isTracing: false,
  validation: null,
  analysis: null,
  trace: null,
  progress: null,
  error: null,
  analysisCache: {},
  traceCache: {},
  validationCache: {},
  searchHistory: [],
};

// Async thunks with improved error handling and caching
export const validateAddress = createAsyncThunk(
  'kyt/validateAddress',
  async ({ address, blockchain }: { address: string; blockchain: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { kyt: KYTState };
      const cacheKey = `${address.toLowerCase()}_${blockchain}`;
      
      // Check cache first
      if (state.kyt.validationCache[cacheKey]) {
        return state.kyt.validationCache[cacheKey];
      }

      const response = await kytService.validateAddress(address, blockchain);

      if (response.success) {
        return { ...response.data, cacheKey };
      } else {
        return rejectWithValue('Address validation failed');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error validating address');
    }
  }
);

export const analyzeAddress = createAsyncThunk(
  'kyt/analyzeAddress',
  async ({ address, blockchain }: { address: string; blockchain: string }, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState() as { kyt: KYTState };
      const cacheKey = `${address.toLowerCase()}_${blockchain}`;
      
      // Check cache first (but allow refresh if data is older than 30 minutes)
      const cachedAnalysis = state.kyt.analysisCache[cacheKey];
      if (cachedAnalysis) {
        const cacheAge = Date.now() - new Date(cachedAnalysis.analysisTimestamp).getTime();
        if (cacheAge < 30 * 60 * 1000) { // 30 minutes
          return cachedAnalysis;
        }
      }

      // Start progress tracking
      dispatch(updateProgress({ 
        step: 'validation', 
        progress: 10, 
        message: 'Validating address format...', 
        isComplete: false 
      }));

      const response = await kytService.analyzeAddress(address, blockchain);

      dispatch(updateProgress({ 
        step: 'analysis', 
        progress: 100, 
        message: 'Analysis complete!', 
        isComplete: true 
      }));

      // Clear progress after a short delay
      setTimeout(() => {
        dispatch(clearProgress());
      }, 2000);

      if (response.success) {
        return { ...response.data, cacheKey };
      } else {
        return rejectWithValue('Address analysis failed');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error analyzing address');
    }
  }
);

export const traceTransactions = createAsyncThunk(
  'kyt/traceTransactions',
  async (
    {
      address,
      blockchain,
      maxSteps = 7,
      includeZeroValue = false
    }: {
      address: string;
      blockchain: string;
      maxSteps?: number;
      includeZeroValue?: boolean;
    },
    { getState, rejectWithValue, dispatch }
  ) => {
    try {
      const state = getState() as { kyt: KYTState };
      const cacheKey = `${address.toLowerCase()}_${blockchain}_${maxSteps}_${includeZeroValue ? 'raw' : 'clean'}`;
      
      // Check cache first, but only use it if it contains non-empty results
      const cachedTrace = state.kyt.traceCache[cacheKey];
      if (cachedTrace && Array.isArray(cachedTrace.traceSteps) && cachedTrace.traceSteps.length > 0) {
        return cachedTrace;
      }

      // Start progress tracking with simulation
      dispatch(updateProgress({ 
        step: 'tracing', 
        progress: 10, 
        message: 'Starting transaction trace...', 
        isComplete: false 
      }));

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        const currentState = getState() as { kyt: KYTState };
        const currentProgress = currentState.kyt.progress?.progress || 10;
        
        if (currentProgress < 90) {
          const newProgress = Math.min(currentProgress + Math.random() * 15, 90);
          const messages = [
            'Analyzing blockchain data...',
            'Tracing transaction paths...',
            'Building network connections...',
            'Calculating risk scores...',
            'Processing transaction history...',
            'Finalizing trace results...'
          ];
          const randomMessage = messages[Math.floor(Math.random() * messages.length)];
          
          dispatch(updateProgress({ 
            step: 'tracing', 
            progress: Math.round(newProgress), 
            message: randomMessage, 
            isComplete: false 
          }));
        }
      }, 500);

      const response = await kytService.traceTransactions(address, blockchain, maxSteps, includeZeroValue);

      // Clear the progress simulation
      clearInterval(progressInterval);

      dispatch(updateProgress({ 
        step: 'tracing', 
        progress: 100, 
        message: 'Transaction trace complete!', 
        isComplete: true 
      }));

      // Clear progress after a short delay
      setTimeout(() => {
        dispatch(clearProgress());
      }, 2000);

      if (response.success) {
        return { ...response.data, cacheKey };
      } else {
        return rejectWithValue('Transaction tracing failed');
      }
    } catch (error: any) {
      // Clear progress simulation on error
      const currentState = getState() as { kyt: KYTState };
      if (currentState.kyt.progress?.step === 'tracing') {
        dispatch(clearProgress());
      }
      return rejectWithValue(error.message || 'Error tracing transactions');
    }
  }
);

const kytSlice = createSlice({
  name: 'kyt',
  initialState,
  reducers: {
    setCurrentAddress: (state, action: PayloadAction<string>) => {
      state.currentAddress = action.payload;
    },
    setCurrentBlockchain: (state, action: PayloadAction<string>) => {
      state.currentBlockchain = action.payload;
    },
    updateProgress: (state, action: PayloadAction<KYTProgress>) => {
      state.progress = action.payload;
    },
    clearProgress: (state) => {
      state.progress = null;
    },
  clearError: (state) => {
    state.error = null;
  },
    clearCurrentResults: (state) => {
      state.validation = null;
      state.analysis = null;
      state.trace = null;
      state.error = null;
      state.progress = null;
    },
    clearCache: (state) => {
      state.analysisCache = {};
      state.traceCache = {};
      state.validationCache = {};
    },
    clearSearchHistory: (state) => {
      state.searchHistory = [];
      // Also clear all cached data when clearing history
      state.analysisCache = {};
      state.traceCache = {};
      state.validationCache = {};
    },
    clearAllData: (state) => {
      // Clear everything - history, cache, current results, and progress
      state.searchHistory = [];
      state.analysisCache = {};
      state.traceCache = {};
      state.validationCache = {};
      state.validation = null;
      state.analysis = null;
      state.trace = null;
      state.error = null;
      state.progress = null;
    },
    addToSearchHistory: (state, action: PayloadAction<{
      address: string;
      blockchain: string;
      type: 'validation' | 'analysis' | 'trace';
    }>) => {
      const newEntry = {
        ...action.payload,
        timestamp: new Date().toISOString()
      };
      
      // Remove duplicate entries and keep only last 50
      state.searchHistory = [
        newEntry,
        ...state.searchHistory
          .filter(h => !(h.address === newEntry.address && h.blockchain === newEntry.blockchain && h.type === newEntry.type))
          .slice(0, 49)
      ];
    },
  },
  extraReducers: (builder) => {
    builder
      // Validate Address
      .addCase(validateAddress.pending, (state) => {
        state.isValidating = true;
        state.error = null;
      })
      .addCase(validateAddress.fulfilled, (state, action) => {
        state.isValidating = false;
        state.validation = action.payload;
        
        // Cache the result
        if (action.payload.cacheKey) {
          state.validationCache[action.payload.cacheKey] = action.payload;
        }
        
        // Add to search history
        state.searchHistory.unshift({
          address: action.payload.address,
          blockchain: action.payload.blockchain,
          timestamp: new Date().toISOString(),
          type: 'validation'
        });
      })
      .addCase(validateAddress.rejected, (state, action) => {
        state.isValidating = false;
        state.error = action.payload as string;
      })
      
      // Analyze Address
      .addCase(analyzeAddress.pending, (state) => {
        state.isAnalyzing = true;
        state.error = null;
      })
      .addCase(analyzeAddress.fulfilled, (state, action) => {
        state.isAnalyzing = false;
        state.analysis = action.payload;
        state.progress = null;
        
        // Cache the result
        if (action.payload.cacheKey) {
          state.analysisCache[action.payload.cacheKey] = action.payload;
        }
        
        // Add to search history
        state.searchHistory.unshift({
          address: action.payload.address,
          blockchain: action.payload.blockchain,
          timestamp: new Date().toISOString(),
          type: 'analysis'
        });
      })
      .addCase(analyzeAddress.rejected, (state, action) => {
        state.isAnalyzing = false;
        state.error = action.payload as string;
        state.progress = null;
      })
      
      // Trace Transactions
      .addCase(traceTransactions.pending, (state) => {
        state.isTracing = true;
        state.error = null;
      })
      .addCase(traceTransactions.fulfilled, (state, action) => {
        state.isTracing = false;
        state.trace = action.payload;
        state.progress = null;
        
        // Cache the result
        if (action.payload.cacheKey) {
          const hasNonEmptySteps = Array.isArray(action.payload.traceSteps) && action.payload.traceSteps.length > 0;
          // Only cache traces that have data to avoid persisting empty results
          if (hasNonEmptySteps) {
            state.traceCache[action.payload.cacheKey] = action.payload;
          } else {
            // Ensure stale empty cache entries are removed so future traces can refetch
            delete state.traceCache[action.payload.cacheKey];
          }
        }
        
        // Add to search history
        state.searchHistory.unshift({
          address: action.payload.rootAddress,
          blockchain: action.payload.blockchain,
          timestamp: new Date().toISOString(),
          type: 'trace'
        });
      })
      .addCase(traceTransactions.rejected, (state, action) => {
        state.isTracing = false;
        state.error = action.payload as string;
        state.progress = null;
      });
  },
});

export const {
  setCurrentAddress,
  setCurrentBlockchain,
  updateProgress,
  clearProgress,
  clearError,
  clearCurrentResults,
  clearCache,
  clearSearchHistory,
  clearAllData,
  addToSearchHistory,
} = kytSlice.actions;

export default kytSlice.reducer;
